import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Animate } from '@/components/Animate'
import useClickOutside from '@/hooks/useClickOutside'

import s from './DropdownMenu.module.css'

const DropdownMenuContext = createContext<{
  isOpen: boolean
  setIsOpen: (open: boolean) => void
} | null>(null)

type DropdownMenuProps = {
  children: ReactNode
  fullWidth?: boolean
  position?: 'left' | 'right'
  disabled?: boolean
}

type DropdownMenuItemProps = {
  children: ReactNode
  destructive?: boolean
  disabled?: boolean
  isActive?: boolean
  onClick?: () => void
}

export const DropdownMenu = ({ children }: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const setRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false))

  useEffect(() => {
    if (containerRef.current) {
      setRef(containerRef.current)
    }
  }, [setRef])

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen }}>
      <div ref={containerRef} className={s.wrapper}>
        {children}
      </div>
    </DropdownMenuContext.Provider>
  )
}

export const DropdownMenuTrigger = ({
  children,
  disabled,
}: DropdownMenuProps) => {
  const context = useContext(DropdownMenuContext)
  if (!context)
    throw new Error('DropdownMenuTrigger must be used inside DropdownMenu')

  const { isOpen, setIsOpen } = context

  return (
    <button
      className={s.trigger}
      data-active={isOpen}
      onClick={(e) => {
        e.stopPropagation()
        setIsOpen(!isOpen)
      }}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

export const DropdownMenuContent = ({
  children,
  fullWidth,
  position = 'right',
}: DropdownMenuProps) => {
  const context = useContext(DropdownMenuContext)
  if (!context)
    throw new Error('DropdownMenuTrigger must be used inside DropdownMenu')

  const { isOpen } = context

  return (
    <Animate
      in={isOpen}
      className={s.transition}
      classNameOut={s.transitionExit}
    >
      <div
        className={`${s.content} ${fullWidth ? s.fullWidth : ''} ${
          s[position]
        }`}
        role="menu"
        aria-orientation="vertical"
      >
        {children}
      </div>
    </Animate>
  )
}

export const DropdownMenuItem = ({
  children,
  destructive,
  onClick,
  isActive,
  disabled = false,
}: DropdownMenuItemProps) => {
  const context = useContext(DropdownMenuContext)
  if (!context)
    throw new Error('DropdownMenuItem must be used inside DropdownMenu')

  return (
    <div
      className={`${s.item} ${destructive ? s.isDestructive : ''} ${
        isActive ? s.isActive : ''
      } ${disabled ? s.disabled : ''}`}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.()
        context.setIsOpen(false)
      }}
    >
      {children}
    </div>
  )
}

DropdownMenu.Trigger = DropdownMenuTrigger
DropdownMenu.Content = DropdownMenuContent
DropdownMenu.Item = DropdownMenuItem
